// Import — drop a JSONL file of events (or paste it) and append it to the log.
// Rendered as panels inside Settings, so the rail stays clear.
//
// Why this exists as a real feature rather than a one-off script: the data format is
// a public contract, so an agent (or a migration off another tool) can generate a
// translated log outside the app and hand it over. That also means real client data
// never has to live in the app's repo to get in front of you.
//
// Safety comes from the same property the v0 import relies on: events carry their
// own ids, so anything already in the log is skipped and re-importing the same file
// is a no-op. Nothing is ever overwritten — this only appends. The server enforces
// it too, with a unique index on the event id, so the guarantee does not depend on
// this screen having counted correctly.
//
// This is also the path your General Text crum data comes in by: export the
// workspace's `v1/events/*.jsonl`, drop the files here.

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button, ConfirmAction, Section } from '../components/common'
import { Icon } from '../components/Icon'
import { api, workspaceSlug } from '../lib/api'
import { parseEvent, type CrumEvent } from '../lib/events'
import { useStore } from '../lib/store'

interface Parsed {
  events: CrumEvent[]
  duplicates: number
  bad: number
  byType: { type: string; count: number }[]
}

function inspect(text: string, applied: Set<string>): Parsed {
  const events: CrumEvent[] = []
  const seen = new Set<string>()
  let duplicates = 0
  let bad = 0
  for (const line of text.split('\n')) {
    if (!line.trim()) continue
    const ev = parseEvent(line)
    if (!ev) {
      bad++
      continue
    }
    // Skip what the projection already has, and de-dupe within the file itself.
    if (applied.has(ev.id) || seen.has(ev.id)) {
      duplicates++
      continue
    }
    seen.add(ev.id)
    events.push(ev)
  }
  const counts = new Map<string, number>()
  for (const ev of events) counts.set(ev.type, (counts.get(ev.type) ?? 0) + 1)
  return {
    events,
    duplicates,
    bad,
    byType: [...counts.entries()]
      .map(([type, count]) => ({ type, count }))
      .sort((a, b) => b.count - a.count || a.type.localeCompare(b.type)),
  }
}

export function ImportPanel() {
  const { state, version } = useStore()
  const navigate = useNavigate()
  void version
  const [text, setText] = useState('')
  const [dragging, setDragging] = useState(false)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState<{ read: number; added: number } | null>(null)
  const [error, setError] = useState('')

  const parsed = text.trim() ? inspect(text, state.applied) : null

  async function readFiles(files: FileList | null) {
    if (!files || files.length === 0) return
    setError('')
    setDone(null)
    try {
      const parts = await Promise.all([...files].map((f) => f.text()))
      setText(parts.join('\n'))
    } catch {
      setError('Could not read that file. You can paste its contents below instead.')
    }
  }

  async function run() {
    if (!parsed || parsed.events.length === 0) return
    setBusy(true)
    setError('')
    try {
      // Send the file as it came, not the de-duplicated subset: the server is the
      // authority on what it already holds, and this screen's count is only a
      // preview folded against the local projection.
      const result = await api.importJsonl(text)
      setDone({ read: result.read, added: result.added })
      setText('')
      // No manual refresh: the append broadcasts, and the store folds what arrives.
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The import failed. Nothing partial was written.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Section
      title="Import events"
      note="one JSON event per line"
      right={
        parsed && parsed.events.length > 0 ? (
          <Button variant="work" onClick={() => void run()}>
            <Icon name="Plus" size={14} /> {busy ? 'Appending…' : `Append ${parsed.events.length}`}
          </Button>
        ) : undefined
      }
    >
      {done && (
        <div
          className="rounded-control text-body mb-3 flex flex-wrap items-center gap-2 border px-3 py-2.5"
          style={{
            borderColor: 'color-mix(in srgb, var(--good) 40%, transparent)',
            background: 'color-mix(in srgb, var(--good) 8%, transparent)',
          }}
        >
          <Icon name="Check" size={14} style={{ color: 'var(--good)' }} />
          <span>
            Read <b>{done.read}</b> {done.read === 1 ? 'event' : 'events'}, added{' '}
            <b>{done.added}</b>
            {done.added !== done.read ? ' (the rest were already in the log)' : ''}.
          </span>
          <button
            type="button"
            onClick={() => navigate('/overview')}
            className="ml-auto font-medium"
            style={{ color: 'var(--accent)' }}
          >
            Go look →
          </button>
        </div>
      )}

      {error && (
        <div
          className="rounded-control text-body mb-3 flex items-start gap-2 border px-3 py-2.5"
          style={{
            borderColor: 'color-mix(in srgb, var(--crit) 40%, transparent)',
            background: 'color-mix(in srgb, var(--crit) 8%, transparent)',
          }}
        >
          <Icon name="Circle" size={14} style={{ color: 'var(--crit)' }} />
          <span>{error}</span>
        </div>
      )}

      <label
        onDragOver={(e) => {
          e.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragging(false)
          void readFiles(e.dataTransfer.files)
        }}
        className="rounded-float flex cursor-pointer flex-col items-center justify-center gap-1.5 border-2 border-dashed py-8 text-center"
        style={{
          borderColor: dragging ? 'var(--marine)' : 'var(--border)',
          background: dragging ? 'var(--work-soft)' : 'var(--panel)',
        }}
      >
        <Icon
          name="ArrowRight"
          size={16}
          style={{ color: 'var(--faint)', transform: 'rotate(90deg)' }}
        />
        <span className="text-body font-medium">Drop a .jsonl file here</span>
        <span className="meta">or click to choose one · nothing is overwritten, only appended</span>
        <input
          type="file"
          accept=".jsonl,.json,.txt,application/json,text/plain"
          multiple
          className="hidden"
          onChange={(e) => void readFiles(e.target.files)}
        />
      </label>

      {/* Paste is the always-works path: a file picker inside the app sandbox can be
          blocked, a textarea never is. */}
      <details className="mt-2">
        <summary className="meta cursor-pointer">…or paste the lines instead</summary>
        <textarea
          value={text}
          onChange={(e) => {
            setText(e.target.value)
            setDone(null)
          }}
          spellCheck={false}
          placeholder={
            '{"id":"evt_…","ts":"…","actor":{…},"type":"org.create","subject":"org_…","data":{…}}'
          }
          className="rounded-control mt-2 h-32 w-full resize-y border p-3 outline-none"
          style={{
            borderColor: 'var(--border)',
            background: 'var(--panel)',
            fontFamily: 'var(--mono)',
            fontSize: 'var(--text-label)',
          }}
        />
      </details>

      {parsed && (
        <div className="mt-3">
          <div className="sechead mb-1.5">
            <h3 className="text-meta font-semibold">In this file</h3>
            <span className="meta">
              {parsed.events.length} new · {parsed.duplicates} already here
              {parsed.bad ? ` · ${parsed.bad} unreadable` : ''}
            </span>
          </div>
          {parsed.bad > 0 && (
            <p className="text-body mb-2" style={{ color: 'var(--warn)' }}>
              {parsed.bad} {parsed.bad === 1 ? 'line was' : 'lines were'} not a valid event and will
              be skipped. Every line needs an <code>id</code>, a <code>type</code>, and a{' '}
              <code>subject</code>.
            </p>
          )}
          {parsed.events.length === 0 ? (
            <p className="text-body" style={{ color: 'var(--faint)' }}>
              Every event in this file is already in the log. Nothing to do.
            </p>
          ) : (
            <div className="rows">
              {parsed.byType.map((row) => (
                <div key={row.type} className="flex items-center gap-3 py-1.5">
                  <span className="meta flex-1">{row.type}</span>
                  <span className="num" style={{ fontSize: 'var(--text-meta)' }}>
                    {row.count}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Section>
  )
}

/**
 * Export. The whole log as JSONL, which is the same format the import above reads
 * and the same one the General Text app writes — so this is the backup, and it is
 * also the exit. Nothing about the data requires this software to read it.
 */
export function ExportPanel() {
  return (
    <Section title="Export everything" note="the whole log, as JSONL">
      <div className="flex flex-wrap items-center gap-3 py-1">
        {/* A plain link, not a fetch: the browser streams the response straight to
            disk, so the size of the log never has to fit in a JS string.

            It has to name the workspace. `/api/export` is a pre-hosted path and
            nothing serves it now, so the one control that promises the data is
            never locked in was downloading a 404 page. Verified against a live
            workspace: the route below answers ndjson. */}
        <a
          href={`/api/w/${encodeURIComponent(workspaceSlug())}/export`}
          download
          className="rounded-control text-body flex items-center gap-1.5 border px-2.5 py-1.5 font-medium"
          style={{ borderColor: 'var(--border)', background: 'var(--panel)' }}
        >
          <Icon name="ArrowRight" size={14} style={{ transform: 'rotate(90deg)' }} />
          Download the log
        </a>
        <p className="note flex-1">
          One JSON event per line, oldest first. Re-importing it anywhere adds nothing that is
          already there, so a copy is a safe thing to keep.
        </p>
      </div>
    </Section>
  )
}

/**
 * The way out of a stale local cache, on the device that has one.
 *
 * Not dev-only, which the rebuild control used to be: the one time it was actually
 * needed was an installed app on a phone painting a months-old projection, and
 * there was nothing to tap. The server's log is untouched — a refold rebuilds
 * identical state — so this is safe to offer to anyone.
 */
export function LocalCachePanel() {
  const rebuild = () => {
    indexedDB.deleteDatabase('crum')
    window.location.reload()
  }
  return (
    <Section title="This device" note="the local copy, not the log">
      <div className="flex flex-wrap items-center gap-3 py-1">
        <span className="note">
          A cached projection lets this browser paint instantly and then fetch only what it missed.
          Discard it and the next load refolds the whole log from the server.
        </span>
        <span className="ml-auto">
          <ConfirmAction
            icon="Trash2"
            title="Discard the cached projection and refold from the server"
            confirmLabel="Rebuild"
            onConfirm={rebuild}
          />
        </span>
      </div>
    </Section>
  )
}

/** Local development only. `import.meta.env.DEV` is dead-stripped from the build,
 *  so none of this can reach a deployment. */
const DEV_ONLY = import.meta.env.DEV

/**
 * Sample data, for working on the app itself. Loading a real log into a dev
 * instance means clearing the sample cast first, and there is no honest way to do
 * that from outside — so it lives next to the import.
 */
export function SampleDataPanel() {
  const { dispatch } = useStore()
  const [busy, setBusy] = useState(false)
  if (!DEV_ONLY) return null

  const seed = async () => {
    setBusy(true)
    try {
      const { seedDemo } = await import('../lib/seed')
      await seedDemo(dispatch)
    } finally {
      setBusy(false)
    }
  }

  /** Throw away the local projection cache and reload. The log on the server is
   *  untouched — this only forces a full refold, which rebuilds identical state. */
  const rebuild = () => {
    indexedDB.deleteDatabase('crum')
    window.location.reload()
  }

  return (
    <Section title="Sample data" note="local development only">
      <div className="flex flex-wrap items-center gap-3 py-1">
        <Button variant="work" onClick={() => void seed()}>
          <Icon name="Plus" size={14} /> {busy ? 'Seeding…' : 'Seed the sample cast'}
        </Button>
        <span className="ml-auto flex items-center gap-2">
          <span className="meta">rebuild the local cache:</span>
          <ConfirmAction
            icon="Trash2"
            title="Discard the cached projection and refold from the server"
            confirmLabel="Rebuild"
            onConfirm={rebuild}
          />
        </span>
      </div>
      <p className="note mt-1">
        Seeding appends events like any other write, so it lands in the real log on this instance.
        Rebuilding only clears the browser cache; nothing on the server changes.
      </p>
    </Section>
  )
}
